import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {AdminService} from '../../services/admin.service';
import Swal from "sweetalert2";
import {Router} from "@angular/router";
import {ImageCroppedEvent} from "ngx-image-cropper";
import 'hammerjs';

@Component({
  selector: 'app-create-news',
  templateUrl: './create-news.component.html',
  styleUrls: ['./create-news.component.scss']
})
export class CreateNewsComponent implements OnInit {
  albanianForm: FormGroup;
  englishForm: FormGroup;
  selectionForm: FormGroup;
  publishDateForm: FormGroup;
  isMainPostForm: FormGroup;
  videoUrlsForm: FormGroup;
  categories: any[] = [];
  pdfFile: File | null = null;
  pdfPreviewUrl: string | null = null;
  mainImageFile: File | null = null;
  additionalImageFiles: File[] = [];
  mainImagePreviewUrl: string | null = null;
  additionalImagePreviewUrls: string[] = [];
  isLoading = false;
  imageChangedEvent: any = '';
  croppedImage: string | null = null;
  showCropper = false;

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    private router: Router
  ) {
    this.albanianForm = this.fb.group({
      title: ['', [Validators.required, noTrailingSpaceValidator]],
      description: ['', Validators.required]
    });

    this.englishForm = this.fb.group({
      title: ['', noTrailingSpaceValidator],
      description: ['']
    });

    this.selectionForm = this.fb.group({
      categoryId: [null],
      subCategoryId: [null],
      nestedSubCategoryId: [null]
    });

    this.publishDateForm = this.fb.group({
      date: ['', Validators.required],
      time: ['', Validators.required],
    });

    this.isMainPostForm = this.fb.group({
      isMainPost: [false],
      status: ['PUBLISHED', Validators.required]
    });

    this.videoUrlsForm = this.fb.group({
      videoUrls: this.fb.array([this.fb.control('')])
    });
  }

  ngOnInit(): void {
    this.loadCategories();

    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().slice(0, 5);

    this.publishDateForm.patchValue({
      date: currentDate,
      time: currentTime,
    });
  }

  get videoUrls(): FormArray {
    return this.videoUrlsForm.get('videoUrls') as FormArray;
  }

  addVideoUrl(): void {
    this.videoUrls.push(this.fb.control(''));
  }

  removeVideoUrl(index: number): void {
    if (this.videoUrls.length > 1) {
      this.videoUrls.removeAt(index);
    }
  }

  loadCategories(): void {
    this.adminService.getAllCategoriesWithTopLevel().subscribe((res) => {
      this.categories = res;
    });
  }

  getSubCategories() {
    const categoryId = this.selectionForm.get('categoryId')?.value;
    const category = this.categories.find((c) => c.id === categoryId);
    return category ? category.subCategories : [];
  }

  getNestedSubCategories() {
    const subCategoryId = this.selectionForm.get('subCategoryId')?.value;
    const subCategories = this.getSubCategories();

    function findSubCategoryRecursively(categories: any[], id: string): any | null {
      for (const category of categories) {
        if (category.id === id) {
          return category;
        } else if (category.subCategories) {
          const nestedResult = findSubCategoryRecursively(category.subCategories, id);
          if (nestedResult) return nestedResult;
        }
      }
      return null;
    }

    const subCategory = findSubCategoryRecursively(subCategories, subCategoryId);
    return subCategory ? subCategory.subCategories : [];
  }

  onMainImageChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.mainImageFile = input.files[0];
      this.mainImagePreviewUrl = URL.createObjectURL(this.mainImageFile);

      this.imageChangedEvent = event;
      this.showCropper = true;
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    if (event.blob) {
      this.convertBlobToBase64(event.blob).then((base64) => {
        this.croppedImage = base64;
      });
    }
  }

  convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  }

  imageLoaded() {
  }

  cropperReady() {
  }

  loadImageFailed() {
    Swal.fire({
      title: 'Error',
      text: 'Failed to load the image.',
      icon: 'error',
      confirmButtonText: 'OK',
    });
  }

  saveCroppedImage() {
    if (!this.croppedImage) {
      console.error("No cropped image available to save.");
      return;
    }

    this.mainImagePreviewUrl = this.croppedImage;
    this.showCropper = false;
    this.mainImageFile = this.dataURLtoFile(this.croppedImage, "cropped-image.png");
  }

  dataURLtoFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type: mime});
  }

  onAdditionalImagesChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.additionalImageFiles = Array.from(input.files);
      this.additionalImagePreviewUrls = this.additionalImageFiles.map((file) =>
        URL.createObjectURL(file)
      );
    }
  }

  onPdfChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSize = 5 * 1024 * 1024; // 5 MB

      if (file.type !== 'application/pdf') {
        Swal.fire({
          title: 'Error',
          text: 'Only PDF files are allowed.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      if (file.size > maxSize) {
        Swal.fire({
          title: 'Error',
          text: 'The PDF file exceeds the maximum size of 5 MB.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      this.pdfFile = file;
      this.pdfPreviewUrl = URL.createObjectURL(file);
    }
  }

  async onSubmit(): Promise<void> {
    if (this.albanianForm.valid && this.englishForm.valid && this.selectionForm.valid) {
      this.isLoading = true;

      const dateValue = this.publishDateForm.get('date')?.value;
      const timeValue = this.publishDateForm.get('time')?.value;
      const [hours, minutes] = timeValue.split(':');

      const publishedDate = new Date(dateValue);
      publishedDate.setHours(Number(hours), Number(minutes));


      const postData = {
        alTitle: this.albanianForm.get('title')?.value,
        alDescription: this.albanianForm.get('description')?.value,
        enTitle: this.englishForm.get('title')?.value,
        enDescription: this.englishForm.get('description')?.value,
        categoryId: this.selectionForm.get('categoryId')?.value,
        subcategoryId: this.selectionForm.get('subCategoryId')?.value,
        childSubcategoryId: this.selectionForm.get('nestedSubCategoryId')?.value,
        publishedDate: publishedDate.toISOString(),
        isMainPost: this.isMainPostForm.get('isMainPost')?.value,
        status: this.isMainPostForm.get('status')?.value,
        mainImageUrl: null,
        additionalImageUrls: [],
        pdfUrl: null,
        videoUrls: this.videoUrls.value.filter((url: string) => url.trim() !== '')
      };

      try {
        const response = await this.adminService.createNews(postData).toPromise();
        const postId = response.id;

        if (this.mainImageFile) {
          const mainImageResponse = await this.adminService.postMainImage(postId, this.mainImageFile).toPromise();
          if (mainImageResponse && mainImageResponse.imageUrl) {
            postData.mainImageUrl = mainImageResponse.imageUrl;
          }
        }

        if (this.additionalImageFiles.length > 0) {
          const additionalImagesResponse = await this.adminService.postAdditionalImages(postId, this.additionalImageFiles).toPromise();
          if (additionalImagesResponse && additionalImagesResponse.imageUrls) {
            postData.additionalImageUrls = additionalImagesResponse.imageUrls;
          }
        }

        if (this.pdfFile) {
          const pdfResponse = await this.adminService.postPdfImage(postId, this.pdfFile).toPromise();
          if (pdfResponse && pdfResponse.pdfUrl) {
            postData.pdfUrl = pdfResponse.pdfUrl;
          }
        }

        Swal.fire({
          title: 'Success!',
          text: 'News created successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          this.router.navigate(['/admin-panel/admin-news']);
        });

      } catch (error) {
        console.error('Error during creation or file upload:', error);
        Swal.fire({
          title: 'Error!',
          text: 'An error occurred while creating the news post.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } finally {
        this.isLoading = false;
      }
    } else {
      this.isLoading = false;
      console.error('Forms are invalid.');
    }
  }
}

export function noTrailingSpaceValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (typeof value === 'string' && value.endsWith(' ')) {
    return { trailingSpace: true };
  }
  return null;
}
